import '../product-card/product-card-display';
import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { Catalog } from '../../common/constants/catalog';
import { PARAM_CATALOG, performSearch } from './thunks/search.thunk';
import { StoreState } from '../../store/store.interface';
import { clearSuggestions } from './store/suggestion.store';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getQueryParam, mapCatalog, isSearchPage } from '../../common/utils/util';
import { html } from 'lit';
import { isLoading, selectSelectedCatalog } from './store/search.selector';
import { setSelectedCatalog } from './store/search.store';
import { TAB_NAMES } from '../tabbed-search/constants';

/**
 * This object represents one of the options of the catalog config selector
 */
export declare interface CatalogConfig {
  label: string;
  id: string;
  catalog: Catalog;
  defaultChecked: boolean;
  isChecked?: boolean;
}

/**
 * The default configuration of the search catalog selector when it is
 * not configured or is an array of length 1.
 */
export const DEFAULT_CATALOG_CONFIG: Array<CatalogConfig> = [
  {
    label: 'View All',
    id: 'catalog-config-option-view-all',
    catalog: Catalog.unified,
    defaultChecked: true,
  },
  {
    label: 'View All',
    id: 'catalog-config-option-view-all-products',
    catalog: Catalog.products,
    defaultChecked: false,
  },
  {
    label: 'PreK-12',
    id: 'catalog-config-option-prek12-adult',
    catalog: Catalog.seg,
    defaultChecked: false,
  },
  {
    label: 'Higher Education',
    id: 'catalog-config-option-higher-education',
    catalog: Catalog.he,
    defaultChecked: false,
  },
  {
    label: 'Topics & Ideas',
    id: 'catalog-config-option-topics-ideas',
    catalog: Catalog.content,
    defaultChecked: false,
  },
];

export const DEFAULT_CATALOG_SELECTOR_NAME = 'catalog-config-option';
export const DEFAULT_CATALOG_SELECTOR_ARIA_LABEL = 'Select the catalog that you want to search in';

/**
 * @csspart container
 * @csspart unified
 * @csspart seg_program
 * @csspart he
 * @csspart content
 *
 * @event search-catalog-selected - A user has chosen a catalog
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail.catalog.label: string
 *        <br><br>event.detail.catalog.id: string
 *        <br><br>event.detail.catalog.catalog: string
 *        <br><br>event.detail.catalog.wasDefaultSelection: boolean
 *        <br><br>event.detail.size: string
 *        <br><br>event.detail.name: string
 *        <br><br>event.detail.ariaLabel: string
 *        <br><br>event.detail.layout: string
 */
@customElement('mhecomm-search-catalog-selector')
export class SearchCatalogSelector extends connect(store)(BaseComponent) {
  /**
   * A JSON String in the format of Array<CatalogConfig> which configures the display settings of
   * the search catalog selector.
   */
  @property({ type: Object })
  catalogs: Array<CatalogConfig> = DEFAULT_CATALOG_CONFIG;

  /**
   * The name to use for the input fields
   */
  @property()
  name: string = DEFAULT_CATALOG_SELECTOR_NAME;

  /**
   * The name to use for the input fields
   */
  @property()
  ariaLabel: string = DEFAULT_CATALOG_SELECTOR_ARIA_LABEL;

  @property()
  categoryId: string | undefined;

  @property({ type: Boolean })
  tabbedSearchEnabled = false;

  @state()
  selectedCatalog?: CatalogConfig;

  @state()
  private _isUserInteraction = false;

  @state()
  private radioAnnouncement = '';

  renderComp() {
    return html`
      <span class="visually-hidden" aria-live="polite"> ${this.radioAnnouncement} </span>
      <div class="accordion" data-automation-id="catalog-accordion" aria-label=${this.ariaLabel}>
        <div class="accordion-item">
          <h3 class="accordion-header" id="catalog">
            <button
              type="button"
              data-bs-toggle="collapse"
              aria-expanded="true"
              class="accordion-button fw-semibold"
              data-bs-target="#catalog-collapse"
              aria-controls="catalog-collapse"
              @click=${this._handleCatalogCollapseToggle}>
              By Collection
            </button>
          </h3>
          <fieldset
            id="catalog-collapse"
            class="accordion-collapse collapse show accordion-body"
            aria-labelledby="catalog"
            role="region"
            data-bs-parent="#catalog-accordion">
            <legend class="visually-hidden">Collection radio buttons</legend>
            ${this.catalogs.map(
              (catalog) => html`
                <div class="form-check pb-2">
                  <input
                    class="form-check-input"
                    type="radio"
                    .checked=${catalog.id === this.selectedCatalog?.id}
                    value=${this.name}
                    id=${catalog.id}
                    @click="${() => this._handleCatalogSelection(catalog)}" />
                  <label class="form-check-label" for="${catalog.id}">${catalog.label}</label>
                </div>
              `
            )}
          </fieldset>
        </div>
      </div>
    `;
  }

  /**
   * Collapse or expands the catalog collection list.
   *
   * @param event The event object
   */
  private _handleCatalogCollapseToggle(event: any) {
    const element = event.target as HTMLElement;
    const collaseId = element.getAttribute('data-bs-target') as string;
    const collapseInstance = this.bsInit(collaseId, window.bootstrap?.Collapse, { toggle: false })?.instance;
    const collapse = this.shadowRoot?.querySelector(collaseId) as HTMLElement;
    if (element.classList.contains('collapsed')) {
      collapse.addEventListener('show.bs.collapse', () => element.classList.remove('collapsed'), { once: true });
      collapseInstance.show();
      element.setAttribute('aria-expanded', 'true');
    } else {
      collapse.addEventListener('hide.bs.collapse', () => element.classList.add('collapsed'), { once: true });
      collapseInstance.hide();
      element.setAttribute('aria-expanded', 'false');
    }
  }

  private _handleCatalogSelection(catalog: CatalogConfig) {
    if (this.selectedCatalog?.id !== catalog.id) {
      this._isUserInteraction = true; // Mark as user interaction
      this.selectedCatalog = catalog;
      this.radioAnnouncement = `${catalog.label} collection selected. Search results have been updated.`;
      this.catalogSelected(catalog); // Emit the event and update the state
      this._isUserInteraction = false; // Reset the flag
    }
  }

  /**
   * When this component loads, it needs to identify the default catalog option, initialize
   * it's own state, and then dispatch an event to update the redux store with the catalog.
   */
  connectedCallback(): void {
    super.connectedCallback();
    DEFAULT_CATALOG_CONFIG.forEach((option) => {
      if (option.id === this.name) {
        option.defaultChecked = true;
      } else {
        option.defaultChecked = false;
      }
    });
    if (this.catalogs.length === 0) {
      this.catalogs = DEFAULT_CATALOG_CONFIG;
    }
    this.catalogs.forEach((catalog) => {
      if (catalog.defaultChecked) {
        this.selectedCatalog = catalog;
      }
    });
    const queryCatalog = getQueryParam(PARAM_CATALOG);
    if (queryCatalog) {
      this.selectedCatalog = this.catalogs.find((catalog) => catalog.catalog === mapCatalog(queryCatalog!));
    }
    if (this.selectedCatalog && !this.isNonProductTabActive()) {
      store.dispatch(setSelectedCatalog(this.selectedCatalog.catalog));
    }
  }

  /**
   * The `bu` URL param only identifies a business unit within the Product tab — it has no
   * value representing Support or Topics & Ideas. When the tabbed coordinator has one of
   * those tabs active, that tab (not this component) owns `selectedCatalog`.
   */
  private isNonProductTabActive(): boolean {
    if (!this.tabbedSearchEnabled) {
      return false;
    }
    const activeTabKey = (store.getState() as StoreState).tabbedSearchActiveTabState?.activeTabKey;
    return activeTabKey === TAB_NAMES.SUPPORT || activeTabKey === TAB_NAMES.ARTICLES;
  }

  /**
   * When redux state changes, this component needs to update it's own internal state to reflect
   * any changes to the selected catalog. This way, this component is linked to the rest of the
   * page in case the selected catalog changes anywhere else.
   *
   * @param state The current snapshot of the redux store state
   */
  stateChanged(state: StoreState) {
    const selectedCatalog = selectSelectedCatalog(state);

    if (!this.selectedCatalog || selectedCatalog != this.selectedCatalog?.catalog) {
      this.selectedCatalog = this.catalogs.find((catalog) => catalog.catalog === selectedCatalog);
    }
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('selectedCatalog') && this.selectedCatalog) {
      this.catalogSelected(this.selectedCatalog);
    }
  }

  /**
   * This method first updated redux with the catalog id provided in the catalogConfig parameter
   * and then dispatches an asynchronous event to redux to update the search results. Redux
   * does this by making an api call. It also emits an event to the datalayer with all of the
   * relevent details.
   *
   * @param catalogConfig The catalog to select
   */
  async catalogSelected(catalogConfig: CatalogConfig) {
    // Captured up front: `_handleCatalogSelection` resets the flag synchronously, and the
    // echoed `updated()` call must not be mistaken for a direct user selection.
    const isUserInteraction = this._isUserInteraction;
    if (isUserInteraction) {
      emit({
        element: this,
        name: 'search-catalog-selected',
        detail: this.analyticDetail(catalogConfig),
        dataLayer: true,
      });
    }
    if (!this.isNonProductTabActive()) {
      await store.dispatch(setSelectedCatalog(catalogConfig.catalog));
    }
    // Under the tabbed coordinator on search pages SearchV3 owns tab-driven searches, so
    // only a direct user selection here may dispatch one.
    if (this.tabbedSearchEnabled && isSearchPage() && !isUserInteraction) {
      return;
    }
    const state = store.getState() as StoreState;
    const isAlreadyLoading = isLoading(state);
    if (isAlreadyLoading) {
      return;
    }
    store.dispatch(performSearch({ start: 1, fieldValueFilter: {}, categoryId: this.categoryId }));
    store.dispatch(clearSuggestions());
  }

  /**
   * This method collects details about the current state of the component that is easily
   * readable by analytics.
   *
   * @param catalogConfig The catalog configuration to get analytics details for.
   *
   * @returns A json object that is easy for analytics to understand.
   */
  analyticDetail(catalogConfig: CatalogConfig) {
    return {
      catalogDetails: {
        label: catalogConfig.label,
        id: catalogConfig.id,
        catalog: catalogConfig.catalog,
        wasDefaultSelection: catalogConfig.defaultChecked,
      },
      name: this.name,
      ariaLabel: this.ariaLabel,
      catalog: catalogConfig.catalog,
      'Refinement Type': 'Narrow Results',
      group: 'By Collection',
      label: catalogConfig.label,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-catalog-selector': SearchCatalogSelector;
  }
}
